import { icons } from '@/entities/payment'
import { isBankLabel as checkIsBankLabel } from 'syncra-storybook/shared/utils'
import { OptionModel } from '@/shared/lib/types'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import clsx from 'clsx'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { OptionBankIcon } from '../icons/banks/OptionBankIcon'

export type BaseSelectProps = {
  name?: string
  value?: string | number | null
  options: OptionModel[]
  onChange?: (value: string | number | null) => void
  className?: string
  size?: 'sm' | 'md'
  label?: string
  error?: string
  disabled?: boolean
  variant?: 'primary' | 'secondary'
  placeholder?: string
}
const paymentLabels = ['СБП', 'Карта', 'Счет'] as const

const isBankLabel = (label: string | number): boolean => {
  return typeof label === 'string' && checkIsBankLabel(label)
}

const isPaymentLabel = (label: string | number): label is (typeof paymentLabels)[number] => {
  return typeof label === 'string' && (paymentLabels as readonly string[]).includes(label)
}

// Компонент для отображения иконок методов оплаты
const PaymentIcon = ({ name }: { name: (typeof paymentLabels)[number] }) => {
  return <span className="flex-shrink-0 w-[18px] h-[18px]">{icons[name]}</span>
}

// Компонент для рендера иконки опции
const OptionIcon = ({ option }: { option: OptionModel }) => {
  // Если есть кастомная иконка, используем её
  if (option.icon) {
    return <span className="flex-shrink-0 w-[18px] h-[18px]">{option.icon}</span>
  }

  // Иначе используем логику для банков и платежей
  if (isBankLabel(option.label)) {
    const bankIcon = <OptionBankIcon name={String(option.label)} />
    // Если иконка банка существует, показываем её, иначе скрываем контейнер
    return bankIcon ? (
      <span className="flex-shrink-0 w-[18px] h-[18px]">{bankIcon}</span>
    ) : null
  }

  if (isPaymentLabel(option.label)) {
    return <PaymentIcon name={option.label} />
  }

  return null
}

export const SelectWithLabel = ({
  value,
  options,
  onChange,
  className,
  size = 'sm',
  variant,
  label,
  disabled,
  placeholder = 'Изменить',
  error,
}: BaseSelectProps) => {
  const isControlled = value !== undefined

  const [internalSelected, setInternalSelected] = useState<OptionModel | undefined>(
    options.find((opt) => opt.value === value)
  )

  useEffect(() => {
    if (isControlled) {
      setInternalSelected(options.find((opt) => opt.value === value))
    }
  }, [value, options, isControlled])

  const selected = isControlled
    ? options.find((opt) => opt.value === value)
    : internalSelected

  const handleChange = (option: OptionModel) => {
    if (!isControlled) {
      setInternalSelected(option)
    }
    onChange?.(option.value)
  }

  const variantBtnClasses =
    variant === 'primary'
      ? 'bg-white dark:bg-card-background text-primary dark:text-primary-dark'
      : 'bg-light-purple dark:bg-dark-purple text-text-secondary placeholder-text-secondary ring-card-border dark:ring-card-border-dark'

  const variantOptionsClasses =
    variant === 'primary'
      ? 'bg-white dark:bg-card-background ring-card-border dark:ring-card-border-dark'
      : 'bg-white dark:bg-dark-bg ring-card-border dark:ring-card-border-dark'

  const variantOptionClasses =
    variant === 'primary' ? 'text-text-main dark:text-white' : 'text-text-secondary '

  const sizeClasses = size === 'sm' ? 'rounded-lg px-3 py-2' : 'rounded-2xl px-4 py-3'

  return (
    <div className={`flex flex-col gap-2 relative ${className || ''}`}>

      <Listbox value={selected || options[0]} onChange={handleChange} disabled={disabled}>
        <div>
          <ListboxButton
            className={clsx(
              'block relative disabled:opacity-75 px-3 py-2 pr-14 ring-card-border dark:cart-card-border-dark ring-1 ring-inset w-full text-sm/6 text-left disabled:cursor-not-allowed',
              'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-card-border',
              sizeClasses,
              variantBtnClasses,
              error && 'ring-error dark:ring-error ring-1'
            )}
          >
            <div className="flex flex-col gap-1">
                {label && (
                  <label className="text-text-secondary dark:text-text-secondary">
                    {label}:
                  </label>
                )}
                <div className="flex items-center gap-3">
                {selected && <OptionIcon option={selected} />}
                <span className="font-klein font-medium text-base text-text-main dark:text-white">
                  {selected ? selected.label : placeholder}
                </span>
                </div>
            </div>

            <ChevronDownIcon
              className="group top-1/2 right-2 absolute w-5 h-5 -translate-y-1/2 pointer-events-none"
              aria-hidden="true"
            />
          </ListboxButton>
          {error && <div className="text-error text-xs text-right">{error}</div>}
        </div>

        <ListboxOptions
          anchor="bottom"
          transition
          className={clsx(
            'p-1 rounded-lg focus:outline-none ring-1 ring-inset w-[var(--button-width)] [--anchor-gap:var(--spacing-1)]',
            'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
            variantOptionsClasses
          )}
        >
          {options.map((option) => (
            <ListboxOption
              key={option.value}
              value={option}
              className={clsx(
                'group flex items-center gap-2 data-[focus]:bg-light-purple dark:data-[focus]:bg-dark-purple px-3 py-1.5 rounded-lg cursor-default select-none'
              )}
            >
              <CheckIcon className="invisible group-data-[selected]:visible size-4" />
              <div className="flex items-center gap-3">
                <OptionIcon option={option} />
                <div
                  className={clsx(
                    'text-sm/6',
                    variantOptionClasses,
                    'group-data-[selected]:text-black'
                  )}
                >
                  {option.label}
                </div>
              </div>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  )
}
